import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
  OnDestroy,
  computed,
  type Signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * A service that detects the operating system theme (light or dark) and
 * exposes it as a signal and observable.
 */
@Injectable({
  providedIn: 'root',
})
export class SystemThemeService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT, { optional: true });

  private windowMatchMedia: MediaQueryList | undefined = undefined;
  private readonly isSystemDark = signal<boolean>(false);

  readonly theme: Signal<'light' | 'dark'> = computed(() =>
    this.isSystemDark() ? 'dark' : 'light',
  );
  readonly theme$ = toObservable(this.theme);

  constructor() {
    this.watchSystemColorScheme();
  }

  private watchSystemColorScheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.windowMatchMedia = this.document?.defaultView?.matchMedia(
      '(prefers-color-scheme: dark)',
    );
    if (!this.windowMatchMedia) {
      return;
    }
    // Set initial value
    this.isSystemDark.set(this.windowMatchMedia.matches);
    // Watch for changes
    this.windowMatchMedia.addEventListener(
      'change',
      this.handleSystemThemeChange,
    );
  }

  private handleSystemThemeChange = (event: MediaQueryListEvent): void => {
    this.isSystemDark.set(event.matches);
  };

  ngOnDestroy(): void {
    this.windowMatchMedia?.removeEventListener(
      'change',
      this.handleSystemThemeChange,
    );
  }
}
